import {
    FC,
    ExtendProps,
    getProp,
    ContainerProps,
    getTexture
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Texture, NineSlicePlane as Pixi9Plane } from 'pixi.js';
import {$Container, ExtendContainerProps} from './Container';
import {Store} from '@gongxigames/framework/core/fragments/store';

export type NineSlicePlaneProps = ExtendContainerProps<{
    /**
     * Texture to use in nine slice plane
     */
    texture: string | Texture;

    /**
     * Border slice lines config. Available configuration types:
     * [top, right, bottom, left]
     * [top+bottom, right+left]
     * top+bottom+right+left
     */
    slices: [number, number, number, number] | [number, number] | number;
}, Pixi9Plane>;

function createInstance(props: NineSlicePlaneProps): Pixi9Plane {
    const textureData = getProp(props.texture);
    const texture = getTexture(textureData);
    const slices = getProp(props.slices);

    if (Array.isArray(slices) && slices.length === 4) {
        return new Pixi9Plane(texture, slices[0], slices[1], slices[2], slices[3]);
    } else if (Array.isArray(slices) && slices.length === 2) {
        return new Pixi9Plane(texture, slices[0], slices[1], slices[0], slices[1]);
    }

    return new Pixi9Plane(texture, slices, slices, slices, slices);
}

/**
 * Pixi NineSlicePlane component.
 * Use `with` and `height` to set container size,
 * Use `slices` to configure borders slice lines.
 */
export const $NineSlicePlane: FC<NineSlicePlaneProps> = (props) => {
    const instance = props.instance ??= createInstance(props);

    const offTexture = Store.effect(() => {
        const textureData = getProp(props.texture);
        const texture = getTexture(textureData);

        instance.texture = texture;
    });

    const offSlices = Store.effect(() => {
        const slices = getProp(props.slices);

        if (Array.isArray(slices) && slices.length === 4) {
            instance.topHeight = slices[0];
            instance.rightWidth = slices[1];
            instance.bottomHeight = slices[2];
            instance.leftWidth = slices[3];

            return;
        } else if (Array.isArray(slices) && slices.length === 2) {
            instance.bottomHeight = instance.topHeight = slices[0];
            instance.leftWidth = instance.rightWidth = slices[1];

            return;
        }

        instance.topHeight =
            instance.rightWidth =
            instance.bottomHeight =
            instance.leftWidth = slices;
    });

    instance.on('destroyed', offTexture);
    instance.on('destroyed', offSlices);

    return $Container(props as ContainerProps);
};

// Helper types
export type ExtendNineSlicePlaneSimple<T> = T & NineSlicePlaneProps;
export type ExtendNineSlicePlane<T> = ExtendProps<T, NineSlicePlaneProps, Pixi9Plane>;

